import { useEffect, useState } from "react";

/**
 * Search debounce.
 *
 * Matching runs in memory, so this is not hiding a slow query — it keeps the
 * results table from re-rendering on every keystroke of a fast typist. Short
 * enough that results still feel immediate.
 */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * `value`, held back until it has stopped changing for `delayMs`.
 *
 * Returns the initial value immediately, so a first render is never blank.
 * Compare the debounced value against the live one to know whether a change
 * is still pending — that difference is what drives the search spinner.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);

  return debounced;
}
